import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - lucide-react types incompatible with TS 4.9.5
import { BarChart3, Activity, Package, Layers, Settings, LogOut, Wifi, WifiOff, Lock, PieChart, ChevronDown } from 'lucide-react';
import { getClientUser, getClientToken, clientLogout, isClientLoggedIn, clientDashboardService, CLIENT_USER_KEY } from '../../services/clientDashboard';
import ClientDashboardMain from './ClientDashboardMain';
import ClientDashboardPerformance from './ClientDashboardPerformance';
import ClientDashboardOrders from './ClientDashboardOrders';
import ClientDashboardConsolidated from './ClientDashboardConsolidated';
import ClientDashboardAnalytics from './ClientDashboardAnalytics';
import ClientDashboardConfig from './ClientDashboardConfig';
import '../../styles/clientDashboard.css';

type Tab = 'consolidated' | 'main' | 'performance' | 'orders' | 'analytics' | 'config';

interface TabDef { key: Tab; label: string; icon: React.ReactNode }

const DEFAULT_TABS: TabDef[] = [
  { key: 'consolidated', label: 'Consolidado', icon: <Layers size={16} /> },
  { key: 'main', label: 'Visão Geral', icon: <BarChart3 size={16} /> },
  { key: 'performance', label: 'Performance', icon: <Activity size={16} /> },
  { key: 'orders', label: 'Pedidos', icon: <Package size={16} /> },
  { key: 'analytics', label: 'Analytics', icon: <PieChart size={16} /> },
  { key: 'config', label: 'Configurações', icon: <Settings size={16} /> },
];

const ClientDashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('consolidated');
  const [periodo, setPeriodo] = useState('hoje');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const user = getClientUser();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ senhaAtual: '', novaSenha: '', confirmar: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Multi-carrier
  const [carriers, setCarriers] = useState<any[]>([]);
  const [switchingCarrier, setSwitchingCarrier] = useState<number | null>(null);
  const [hasMultipleCarriers, setHasMultipleCarriers] = useState(false);
  const [showCarrierDropdown, setShowCarrierDropdown] = useState(false);
  const carrierDropdownRef = useRef<HTMLDivElement>(null);

  // Preferências do dashboard
  const [visibleTabs, setVisibleTabs] = useState<TabDef[]>(DEFAULT_TABS);

  useEffect(() => {
    if (!isClientLoggedIn()) {
      navigate('/cliente/login');
      return;
    }
    if (user?.senha_temporaria) {
      setShowPasswordModal(true);
    }
    // Verificar se tem múltiplas transportadoras
    clientDashboardService.getMinhasTransportadoras()
      .then(res => {
        if (res.success && res.data && res.data.length > 1) {
          setHasMultipleCarriers(true);
          setCarriers(res.data);
        }
      })
      .catch(() => {});
    // Carregar preferências do dashboard
    clientDashboardService.getPreferencias()
      .then(res => {
        const prefs = res.data;
        if (!prefs) return;
        // Aplicar período padrão
        if (prefs.defaultPeriodo) {
          setPeriodo(prefs.defaultPeriodo);
        }
        // Aplicar ordem e visibilidade das abas
        if (prefs.tabOrder && Array.isArray(prefs.tabOrder)) {
          const ordered: TabDef[] = [];
          prefs.tabOrder.forEach((t: any) => {
            if (t.visible !== false) {
              const def = DEFAULT_TABS.find(d => d.key === t.key);
              if (def) ordered.push(def);
            }
          });
          // Adicionar abas que não estão nas preferências (ex: config sempre visível)
          DEFAULT_TABS.forEach(d => {
            if (!prefs.tabOrder.find((t: any) => t.key === d.key)) {
              ordered.push(d);
            }
          });
          // Config sempre no final e sempre visível
          const configIdx = ordered.findIndex(t => t.key === 'config');
          if (configIdx === -1) {
            ordered.push(DEFAULT_TABS.find(d => d.key === 'config')!);
          }
          if (ordered.length > 0) {
            setVisibleTabs(ordered);
          }
        }
      })
      .catch(() => {});
  }, [navigate]);

  // WebSocket para tempo real
  useEffect(() => {
    const token = getClientToken();
    if (!token) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_ENVIRONMENT === 'development'
      ? 'localhost:3001'
      : 'api-confereai.robotia.com.br';
    const wsUrl = `${wsProtocol}//${wsHost}/ws?token=${token}`;

    let reconnectTimeout: NodeJS.Timeout;
    let pingInterval: NodeJS.Timeout;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          // Inscrever no canal do dashboard
          ws.send(JSON.stringify({ type: 'subscribe', channels: ['client-dashboard', 'conferencias'] }));
          // Ping para manter conexão ativa
          pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'broadcast' && (msg.channel === 'client-dashboard' || msg.channel === 'conferencias')) {
              // Atualizar dashboard quando receber evento
              setRefreshKey(k => k + 1);
            }
          } catch (e) {
            // ignorar mensagens inválidas
          }
        };

        ws.onclose = () => {
          setWsConnected(false);
          clearInterval(pingInterval);
          // Reconectar após 5 segundos
          reconnectTimeout = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
          setWsConnected(false);
        };
      } catch (err) {
        // Fallback: sem WebSocket, usar polling
        setWsConnected(false);
      }
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (carrierDropdownRef.current && !carrierDropdownRef.current.contains(event.target as Node)) {
        setShowCarrierDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fallback: polling a cada 30s quando WebSocket não está conectado
  useEffect(() => {
    if (wsConnected) return;

    const interval = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [wsConnected]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordForm.novaSenha !== passwordForm.confirmar) {
      setPasswordError('As senhas não coincidem.');
      return;
    }
    if (passwordForm.novaSenha.length < 8) {
      setPasswordError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }

    setPasswordSaving(true);
    try {
      await clientDashboardService.changePassword(passwordForm.senhaAtual, passwordForm.novaSenha);
      setPasswordSuccess(true);
      // Atualizar user em localStorage sem senha_temporaria
      const currentUser = getClientUser();
      if (currentUser) {
        currentUser.senha_temporaria = false;
        localStorage.setItem(CLIENT_USER_KEY, JSON.stringify(currentUser));
      }
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
        setPasswordForm({ senhaAtual: '', novaSenha: '', confirmar: '' });
      }, 2000);
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Erro ao alterar senha.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    clientLogout();
    navigate('/cliente/login');
  }, [navigate]);

  const handleSwitchCarrier = async (empresaId: number) => {
    if (empresaId === user?.empresa_id || switchingCarrier !== null) return;
    setSwitchingCarrier(empresaId);
    try {
      const res = await clientDashboardService.trocarTransportadora(empresaId);
      if (res.success) {
        setShowCarrierDropdown(false);
        // Recarregar para atualizar o contexto completo
        window.location.reload();
      }
    } catch (err) {
      console.error('Erro ao trocar transportadora:', err);
    } finally {
      setSwitchingCarrier(null);
    }
  };

  const handlePeriodoChange = (p: string) => {
    setPeriodo(p);
    setDataInicio('');
    setDataFim('');
  };

  const handleCustomDate = () => {
    if (dataInicio && dataFim) {
      setPeriodo('personalizado');
    }
  };

  const filters = periodo === 'personalizado' && dataInicio && dataFim
    ? { periodo: 'personalizado', dataInicio, dataFim }
    : { periodo };

  const renderContent = () => {
    switch (activeTab) {
      case 'main':
        return <ClientDashboardMain filters={filters} refreshKey={refreshKey} />;
      case 'performance':
        return <ClientDashboardPerformance filters={filters} refreshKey={refreshKey} />;
      case 'orders':
        return <ClientDashboardOrders refreshKey={refreshKey} />;
      case 'analytics':
        return <ClientDashboardAnalytics filters={filters} refreshKey={refreshKey} />;
      case 'consolidated':
        return <ClientDashboardConsolidated filters={filters} refreshKey={refreshKey} />;
      case 'config':
        return <ClientDashboardConfig />;
      default:
        return null;
    }
  };

  return (
    <div className="client-dashboard-layout">
      {showPasswordModal && (
        <div className="client-dashboard-modal-overlay">
          <div className="client-dashboard-modal">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <Lock size={24} style={{ color: '#3b82f6' }} />
              <h2 style={{ margin: 0, fontSize: 18, color: '#e2e8f0' }}>Alterar Senha</h2>
            </div>
            <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 20 }}>
              {user?.senha_temporaria
                ? 'Sua senha é temporária. Por segurança, altere-a antes de continuar.'
                : 'Digite sua senha atual e a nova senha.'}
            </p>

            {passwordError && (
              <div className="client-dashboard-error-message" style={{ marginBottom: 16 }}>{passwordError}</div>
            )}
            {passwordSuccess && (
              <div style={{
                background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: 8, padding: '12px 16px', color: '#4ade80', fontSize: 14, marginBottom: 16, textAlign: 'center'
              }}>
                Senha alterada com sucesso!
              </div>
            )}

            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>Senha Atual</label>
                <input
                  type="password"
                  className="client-dashboard-config-input"
                  value={passwordForm.senhaAtual}
                  onChange={e => setPasswordForm(f => ({ ...f, senhaAtual: e.target.value }))}
                  required
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>Nova Senha</label>
                <input
                  type="password"
                  className="client-dashboard-config-input"
                  value={passwordForm.novaSenha}
                  onChange={e => setPasswordForm(f => ({ ...f, novaSenha: e.target.value }))}
                  required
                  minLength={8}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 4 }}>Confirmar Nova Senha</label>
                <input
                  type="password"
                  className="client-dashboard-config-input"
                  value={passwordForm.confirmar}
                  onChange={e => setPasswordForm(f => ({ ...f, confirmar: e.target.value }))}
                  required
                  minLength={8}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="submit"
                  className="client-dashboard-btn client-dashboard-btn-primary"
                  disabled={passwordSaving}
                  style={{ flex: 1 }}
                >
                  {passwordSaving ? 'Salvando...' : 'Alterar Senha'}
                </button>
                {!user?.senha_temporaria && (
                  <button
                    type="button"
                    className="client-dashboard-btn"
                    onClick={() => { setShowPasswordModal(false); setPasswordError(''); }}
                    style={{ background: 'rgba(148,163,184,0.1)', color: '#94a3b8', border: '1px solid rgba(148,163,184,0.2)' }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <header className="client-dashboard-header">
        <div className="client-dashboard-header-left">
          <img src="/images/logo-sidebar.png" alt="ConfereAí" className="client-dashboard-logo" />
          <span className="client-dashboard-company">
            {user?.cliente_razao_social || user?.nome || 'Dashboard'}
          </span>
        </div>
        <div className="client-dashboard-header-right">
          {hasMultipleCarriers && (
            <div className="client-dashboard-carrier-selector" ref={carrierDropdownRef}>
              <button
                className="client-dashboard-carrier-selector-btn"
                onClick={() => setShowCarrierDropdown(!showCarrierDropdown)}
                disabled={switchingCarrier !== null}
              >
                {(() => {
                  const current = carriers.find((c: any) => c.empresa_id === user?.empresa_id);
                  const logoSrc = current?.logomarca_base64 || current?.logomarca_url;
                  return (
                    <>
                      <div className="client-dashboard-carrier-selector-logo">
                        {logoSrc ? (
                          <img src={logoSrc} alt={current?.empresa_nome} />
                        ) : (
                          <span>{(current?.empresa_nome || user?.empresa_nome || '?').charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <span className="client-dashboard-carrier-selector-name">
                        {current?.empresa_nome || user?.empresa_nome}
                      </span>
                      <ChevronDown size={14} className={`client-dashboard-carrier-chevron ${showCarrierDropdown ? 'open' : ''}`} />
                    </>
                  );
                })()}
              </button>
              {showCarrierDropdown && (
                <div className="client-dashboard-carrier-dropdown">
                  <div className="client-dashboard-carrier-dropdown-title">Transportadoras</div>
                  {carriers.map((c: any) => {
                    const isActive = c.empresa_id === user?.empresa_id;
                    const logoSrc = c.logomarca_base64 || c.logomarca_url;
                    return (
                      <button
                        key={c.empresa_id}
                        className={`client-dashboard-carrier-dropdown-item ${isActive ? 'active' : ''}`}
                        onClick={() => handleSwitchCarrier(c.empresa_id)}
                        disabled={isActive || switchingCarrier !== null}
                      >
                        <div className="client-dashboard-carrier-dropdown-logo">
                          {logoSrc ? (
                            <img src={logoSrc} alt={c.empresa_nome} />
                          ) : (
                            <span>{(c.empresa_nome || '?').charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="client-dashboard-carrier-dropdown-info">
                          <span className="client-dashboard-carrier-dropdown-name">
                            {c.empresa_nome || c.empresa_razao_social}
                          </span>
                        </div>
                        {isActive && <span className="client-dashboard-carrier-dropdown-badge">Atual</span>}
                        {switchingCarrier === c.empresa_id && (
                          <span className="client-login-carrier-spinner" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {!hasMultipleCarriers && user?.empresa_nome && (
            <span className="client-dashboard-carrier-badge">{user.empresa_nome}</span>
          )}
          <div className="client-dashboard-live-indicator" title={wsConnected ? 'WebSocket conectado' : 'Atualizando via polling'}>
            {wsConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            <span className="client-dashboard-live-dot" />
            TEMPO REAL
          </div>
          <button className="client-dashboard-user-btn" onClick={handleLogout}>
            <LogOut size={14} />
            Sair
          </button>
        </div>
      </header>

      <nav className="client-dashboard-nav">
        {visibleTabs.map(tab => (
          <button
            key={tab.key}
            className={`client-dashboard-nav-item ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="client-dashboard-content">
        {activeTab !== 'config' && activeTab !== 'orders' && (
          <div className="client-dashboard-filters">
            {['hoje', 'semana', 'mes'].map(p => (
              <button
                key={p}
                className={`client-dashboard-filter-btn ${periodo === p ? 'active' : ''}`}
                onClick={() => handlePeriodoChange(p)}
              >
                {p === 'hoje' ? 'Hoje' : p === 'semana' ? 'Semana' : 'Mês'}
              </button>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
              <input
                type="date"
                className="client-dashboard-config-input"
                value={dataInicio}
                onChange={e => setDataInicio(e.target.value)}
                style={{ width: 150, padding: '6px 10px', fontSize: 13 }}
              />
              <span style={{ color: '#64748b', fontSize: 13 }}>até</span>
              <input
                type="date"
                className="client-dashboard-config-input"
                value={dataFim}
                onChange={e => setDataFim(e.target.value)}
                style={{ width: 150, padding: '6px 10px', fontSize: 13 }}
              />
              <button
                className={`client-dashboard-filter-btn ${periodo === 'personalizado' ? 'active' : ''}`}
                onClick={handleCustomDate}
                disabled={!dataInicio || !dataFim}
                style={{ opacity: (!dataInicio || !dataFim) ? 0.5 : 1 }}
              >
                Filtrar
              </button>
            </div>
          </div>
        )}

        <div className="client-dashboard-tab-content" key={`${activeTab}-${refreshKey}`}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardLayout;
